import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  useToast,
} from '@adop/ui';
import { Copy, Globe, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { domainRequestSchema } from '@adop/shared';
import type { DomainResponse, DomainStatus } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { storefrontUrlFor } from '../../lib/storefront';
import { useAsync } from '../../lib/useAsync';
import { getLocation } from '../../lib/data';
import { apiFetch, postJson, errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { SlugInput } from '../../components/SlugInput';
import { LoadingBlock } from '../../components/LoadingState';

const STATUS_VARIANT: Record<DomainStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  pending_dns: 'warning',
  pending_ssl: 'warning',
  failed: 'danger',
  removed: 'default',
};

export function DomainPage() {
  const { location: workspaceLocation, canOwn } = useWorkspace();
  const { toast } = useToast();
  const locationState = useAsync(
    () => (workspaceLocation ? getLocation(workspaceLocation.id) : Promise.resolve(null)),
    [workspaceLocation?.id],
  );
  const domainsState = useAsync(async () => {
    if (!workspaceLocation) return [] as DomainResponse[];
    const response = await apiFetch<{ domains: DomainResponse[] }>(
      `/api/platform/locations/${workspaceLocation.id}/domains`,
    );
    return response.domains;
  }, [workspaceLocation?.id]);

  const [slugOpen, setSlugOpen] = useState(false);
  const [slugDraft, setSlugDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DomainResponse | null>(null);

  if (!workspaceLocation) {
    return <EmptyState icon={<Globe className="h-6 w-6" />} title="Select a location" />;
  }
  if (locationState.loading || !locationState.data) {
    return <LoadingBlock />;
  }

  const location = locationState.data;
  const currentUrl = storefrontUrlFor(location.slug);

  const changeSlug = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson(`/api/platform/locations/${location.id}/slug`, { slug: slugDraft });
      toast({ title: 'Web address updated', variant: 'success' });
      setSlugOpen(false);
      locationState.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const addDomain = async () => {
    const parsed = domainRequestSchema.safeParse({ hostname });
    if (!parsed.success) {
      setError('Enter a valid domain, e.g. order.example.com');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postJson<DomainResponse>(`/api/platform/locations/${location.id}/domains`, {
        hostname: parsed.data.hostname,
      });
      toast({ title: 'Domain added', variant: 'success' });
      setAddOpen(false);
      setHostname('');
      domainsState.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const refresh = async (domainId: string) => {
    try {
      await apiFetch<DomainResponse>(`/api/platform/domains/${domainId}`);
      toast({ title: 'Status refreshed', variant: 'success' });
      domainsState.reload();
    } catch (cause) {
      toast({ title: 'Could not refresh', description: errorMessage(cause), variant: 'error' });
    }
  };

  const removeDomain = async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await apiFetch(`/api/platform/domains/${removeTarget.domain.id}`, { method: 'DELETE' });
      toast({ title: 'Domain removed', variant: 'success' });
      setRemoveTarget(null);
      domainsState.reload();
    } catch (cause) {
      toast({ title: 'Could not remove', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Copied', variant: 'success' });
    } catch {
      toast({ title: 'Copy failed', variant: 'error' });
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Domain"
        description="Your adop web address and any custom domains."
        action={
          canOwn ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Add custom domain
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>adop web address</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <a href={currentUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-zinc-900 underline">
            {currentUrl}
          </a>
          {canOwn ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSlugDraft(location.slug);
                setSlugOpen(true);
              }}
            >
              Change web address
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <div className="space-y-3">
        {domainsState.loading ? (
          <LoadingBlock />
        ) : (domainsState.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Globe className="h-6 w-6" />}
            title="No custom domains"
            description="Add a domain like order.yourrestaurant.com and follow the DNS steps."
          />
        ) : (
          (domainsState.data ?? []).map((entry) => (
            <Card key={entry.domain.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>{entry.domain.hostname}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS_VARIANT[entry.domain.status]}>{entry.domain.status.replace('_', ' ')}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => void refresh(entry.domain.id)}>
                      <RefreshCw className="h-4 w-4" /> Refresh
                    </Button>
                    {canOwn ? (
                      <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(entry)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                  DNS records to add
                </p>
                <div className="space-y-2">
                  {entry.dns.map((record, index) => (
                    <div key={index} className="rounded-lg border border-zinc-200 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="outline">{record.record_type}</Badge>
                        <span className="text-zinc-500">Name</span>
                        <code className="rounded bg-zinc-100 px-1.5 py-0.5">{record.name}</code>
                        <Button variant="ghost" size="sm" onClick={() => void copy(record.name)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <span className="text-zinc-500">Value</span>
                        <code className="rounded bg-zinc-100 px-1.5 py-0.5">{record.value}</code>
                        <Button variant="ghost" size="sm" onClick={() => void copy(record.value)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <p className="mt-2 text-xs text-zinc-500">{record.note}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Modal
        open={slugOpen}
        onClose={() => setSlugOpen(false)}
        title="Change web address"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setSlugOpen(false)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void changeSlug()}>
              Change address
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert variant="info">
            The old address will keep redirecting to the new one for 30 days, then stop working.
          </Alert>
          <SlugInput value={slugDraft} onChange={setSlugDraft} locationId={location.id} />
        </div>
      </Modal>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add custom domain"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void addDomain()}>
              Add domain
            </Button>
          </>
        }
      >
        <Input
          label="Domain"
          value={hostname}
          onChange={(event) => setHostname(event.target.value.toLowerCase())}
          placeholder="order.example.com"
        />
        <p className="mt-2 text-xs text-zinc-500">
          After adding, we show the DNS records to create at your registrar. SSL is issued automatically.
        </p>
      </Modal>

      <ConfirmDialog
        open={removeTarget !== null}
        title={`Remove ${removeTarget?.domain.hostname ?? ''}?`}
        message="The domain will stop serving your storefront."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => void removeDomain()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
